/**
 * User Profile Page
 *
 * Displays authenticated user information
 * Provides logout functionality
 */

import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

export function ProfilePage() {
  const navigate = useNavigate();
  const { user, donor, isDonorLoading, logout } = useAuth();

  const handleLogout = () => {
    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-6 text-center">My Profile</h1>

        {user && (
          <div className="space-y-8">
            {/* Auth0 User Info */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Authentication Info</h2>
              <div className="space-y-4">
                {user.picture && (
                  <div className="text-center">
                    <img
                      src={user.picture}
                      alt={user.name || 'Profile'}
                      className="w-24 h-24 rounded-full mx-auto border-4 border-primary-200"
                      referrerPolicy="no-referrer"
                      crossOrigin="anonymous"
                    />
                  </div>
                )}

                {user.name && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Auth0 Name
                    </label>
                    <p className="text-gray-900 bg-gray-50 p-3 rounded-lg">{user.name}</p>
                  </div>
                )}

                {user.email && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <div className="flex items-center gap-2">
                      <p className="text-gray-900 bg-gray-50 p-3 rounded-lg flex-1">{user.email}</p>
                      {user.email_verified && (
                        <span className="flex items-center gap-1 text-sm text-green-600 bg-green-50 px-3 py-2 rounded-lg whitespace-nowrap">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                          Verified
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Donor Info */}
            <div className="border-t pt-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Donor Profile</h2>
              {isDonorLoading ? (
                <p className="text-gray-600">Loading donor profile...</p>
              ) : donor ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {donor.donorId && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Donor ID
                        </label>
                        <p className="text-gray-900 bg-gray-50 p-3 rounded-lg">{donor.donorId}</p>
                      </div>
                    )}
                    {donor.completeName && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Complete Name
                        </label>
                        <p className="text-gray-900 bg-gray-50 p-3 rounded-lg">
                          {donor.completeName}
                        </p>
                      </div>
                    )}
                    {donor.eMail && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Donor Email
                        </label>
                        <p className="text-gray-900 bg-gray-50 p-3 rounded-lg">{donor.eMail}</p>
                      </div>
                    )}
                    {donor.phoneNumber && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Phone Number
                        </label>
                        <p className="text-gray-900 bg-gray-50 p-3 rounded-lg">
                          {donor.phoneNumber}
                        </p>
                      </div>
                    )}
                  </div>

                  {(donor.addressLine1 || donor.city || donor.state) && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Address
                      </label>
                      <div className="text-gray-900 bg-gray-50 p-3 rounded-lg space-y-1">
                        {donor.addressLine1 && <p>{donor.addressLine1}</p>}
                        {donor.addressLine2 && <p>{donor.addressLine2}</p>}
                        {(donor.city || donor.state || donor.postalCode) && (
                          <p>
                            {donor.city && donor.city}
                            {donor.city && donor.state && ', '}
                            {donor.state && donor.state}
                            {donor.postalCode && ` ${donor.postalCode}`}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {donor.occupation && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Occupation
                      </label>
                      <p className="text-gray-900 bg-gray-50 p-3 rounded-lg">{donor.occupation}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
                  <div className="flex items-start gap-3">
                    <svg
                      className="h-6 w-6 text-amber-400 shrink-0 mt-0.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                    <div className="flex-1">
                      <h3 className="font-medium text-amber-900 mb-1">
                        Account Validation In Progress
                      </h3>
                      <p className="text-sm text-amber-800">
                        Your account is being validated by an administrator. Once approved, your
                        complete donor profile will be available here.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="border-t pt-6">
              <button
                onClick={handleLogout}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
